// ============================================
// THEME
// ============================================

/** Configurable brand color for assignment wizard */
export const ASSIGNMENT_THEME_COLOR = '#22C55E'; // Green-500

// ============================================
// ASSIGNMENT STATUS
// ============================================

// Values must match backend: 'draft', 'published', 'closed'
// Additional client-side statuses for wizard navigation
export enum AssignmentStatus {
  DRAFT = 'draft',
  CONTEXT_COMPLETED = 'context_completed', // Client-side only
  OBJECTIVE_COMPLETED = 'objective_completed', // Client-side only
  QUESTIONS_GENERATED = 'questions_generated', // Client-side only
  SETTINGS_COMPLETED = 'settings_completed', // Client-side only
  PUBLISHED = 'published',
}

// Maps assignment status to the maximum step user can navigate to
// New flow: 1=Configure, 2=Upload, 3=Questions, 4=Settings, 5=Ready
export const STATUS_TO_MAX_STEP: Record<AssignmentStatus, number> = {
  [AssignmentStatus.DRAFT]: 1,
  [AssignmentStatus.CONTEXT_COMPLETED]: 2,
  [AssignmentStatus.OBJECTIVE_COMPLETED]: 2, // Still on Upload (generating inline)
  [AssignmentStatus.QUESTIONS_GENERATED]: 4, // Can go to Questions and Settings
  [AssignmentStatus.SETTINGS_COMPLETED]: 5, // Can go to Ready
  [AssignmentStatus.PUBLISHED]: 5, // On Ready
};

// ============================================
// GENERATION STATUS
// ============================================

export enum GenerationStatus {
  IDLE = 'idle',
  GENERATING_QUESTIONS = 'generating_questions',
  GENERATING_SCORING_GUIDE = 'generating_scoring_guide',
  FAILED = 'failed',
}

export interface GenerationStatusResponse {
  generationStatus: GenerationStatus;
  generationError: string | null;
}

// ============================================
// INPUT DATA
// ============================================

export const QUESTION_COUNT_OPTIONS = [1, 2, 3, 5, 10, 15, 20] as const;
export const DEFAULT_QUESTION_COUNT = 5;

export interface AssignmentInputData {
  grade: number | null;
  tier1SubjectId: string | null;
  tier2SubjectId: string | null;
  state: string | null;
  objective: string;
  questionCount: number;
}

export const DEFAULT_INPUT_DATA: AssignmentInputData = {
  grade: null,
  tier1SubjectId: null,
  tier2SubjectId: null,
  state: null,
  objective: '',
  questionCount: DEFAULT_QUESTION_COUNT,
};

// ============================================
// RESOURCE (uploaded files)
// ============================================

export interface AssignmentResource {
  id: string;
  filename: string;
  mimeType: string;
  size: number;
  uploadedAt: Date;
}

// ============================================
// QUESTION TYPES
// ============================================

export enum QuestionType {
  MULTIPLE_CHOICE = 'multiple_choice',
  SHORT_ANSWER = 'short_answer',
  ESSAY = 'essay',
}

export interface MultipleChoiceOption {
  id: string;
  text: string;
  isCorrect: boolean;
}

export interface AssignmentQuestion {
  id: string;
  type: QuestionType;
  questionText: string;
  options: MultipleChoiceOption[];
  points: number;
  order: number;
  scoringGuideItemId: string | null;
}

// ============================================
// GENERATION PROGRESS STEPS
// ============================================

export interface GenerationProgressStep {
  key: string;
  label: string;
}

export const GENERATION_PROGRESS_STEPS: GenerationProgressStep[] = [
  { key: 'analyzing', label: 'Analyzing your objective...' },
  { key: 'drafting', label: 'Drafting high-quality questions...' },
  { key: 'formatting', label: 'Formatting and reviewing questions...' },
  { key: 'scoring', label: 'Generating scoring guide...' },
  { key: 'finalizing', label: 'Finalizing your assignment...' },
];
